/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  toRosterOfficialRow,
  toRosterPlayerRow,
  type RosterOfficialRow,
  type RosterPlayerRow,
} from '@sportninja/common/features/competition/operations/utils/rosterRows'
import { getTeamOfficialTypesBySportId } from '@sportninja/common/selectors/types'
import { useSelector } from 'src/hooks/useRedux'
import { useColumnVisibility } from 'src/hooks/useColumnVisibility'
import ColumnChooser from 'src/components/ts-components/ColumnChooser'
import { paragraphSmall, weightSemiBold } from 'src/components/css'
import { useRosterContext } from 'src/pages/Team/UnifiedRoster/context/RosterContext'
import { usePositionOptions } from 'src/features/team/player/hooks/useRosterPlayer'
import { useInlinePlayerEdit } from '../../hooks/useInlinePlayerEdit'
import { useInlineOfficialEdit } from '../../hooks/useInlineOfficialEdit'
import { useRosterPlayersColumns } from '../../grid/useRosterPlayersColumns'
import { useRosterOfficialsColumns } from '../../grid/useRosterOfficialsColumns'
import type { RosterRowAction } from './RosterRowActions'
import RosterGrid from './RosterGrid'
import RosterModals, { type RosterModalState } from './RosterModals'
import {
  applyHiddenColumns,
  chooserColumnsOf,
  ROSTER_LOCKED_COLUMN_IDS,
} from '../../utils/rosterColumnVisibility'
import type { OrganizationDetails } from '@sportninja/common/services/organization/readOrganization'
import {
  useRosterPermissions,
  type RosterCapabilities,
} from '../../hooks/useRosterPermissions'

const translate = t as (key: string, options?: unknown) => string

interface RosterSectionProps {
  teamId: string
  /** Shown in the Add Player search modal's title. */
  teamName: string
  sportId: string | null
  canEdit: boolean
  /**
   * Owning organization. Needed only for the Add gates — the shared permission
   * helpers read `sync_salesforce` and `id` from it. Null while it loads.
   */
  organization: OrganizationDetails | null
  /**
   * Bumped by the panel header's Add Player button. The button lives up there
   * (next to Edit Team) while the modal must live in here, inside
   * `RosterProvider` — a counter is the simplest signal that crosses that
   * boundary and still fires on repeat clicks.
   */
  addPlayerSignal?: number
  /**
   * Reports roster counts up to the panel's stat strip, which renders in
   * `PanelHeader` — outside `RosterProvider`, so it cannot read them itself.
   */
  onCountsChange?: (counts: RosterCounts) => void
  /**
   * Reports the visible tab up to the panel header, whose Add button switches
   * between Add Player and Add Team Official. Same round trip as the counts —
   * the header renders outside `RosterProvider`.
   */
  onTabChange?: (tab: RosterTab) => void
  /**
   * Reports whether Add Player / Add Team Official are permitted. The button
   * lives in `PanelHeader`, outside `RosterProvider`, so it cannot evaluate
   * these itself — the same round trip as `onCountsChange`.
   */
  onCapabilitiesChange?: (capabilities: RosterCapabilities) => void
  /**
   * The full-screen shell's layer while the tab is full screen, else absent.
   * Forwarded to the roster modals so they stack above the shell.
   */
  hostZIndex?: number
}

export interface RosterCounts {
  players: number
  spares: number
  officials: number
}

export type RosterTab = 'players' | 'officials'

/**
 * The team's roster inside the Operations panel: Players and Spares as two
 * tables under one tab, Team Officials under the other — the layout from the
 * approved design.
 *
 * Reads everything from `RosterContext`, which the parent already mounts, so
 * this fires no requests of its own. Every mutating action reuses the existing
 * player/official flows rather than reimplementing them.
 */
const RosterSection = ({
  teamId,
  teamName,
  sportId,
  canEdit,
  organization,
  addPlayerSignal = 0,
  onCountsChange,
  onTabChange,
  onCapabilitiesChange,
  hostZIndex,
}: RosterSectionProps) => {
  'use memo'

  const {
    players,
    affiliates,
    teamOfficials,
    selectedRosterId,
    loading,
    shouldShowWaiver,
  } = useRosterContext()

  const [tab, setTab] = useState<RosterTab>('players')

  const selectTab = (next: RosterTab) => {
    setTab(next)
    onTabChange?.(next)
  }

  // Report the starting tab on mount. This section remounts per team (keyed in
  // OperationsTab) and always starts on Players, so without this the header
  // would keep showing the tab the PREVIOUS team was left on.
  useEffect(() => {
    onTabChange?.('players')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [modal, setModal] = useState<RosterModalState>({ kind: 'none' })

  // Opens the add modal when the header button fires. Compared against the
  // value seen on mount rather than against 0: selecting another team remounts
  // this section while the counter keeps its value, and a bare `> 0` test would
  // spring the modal open on arrival. Which entity gets added follows the
  // visible tab, matching the header button's own label. Players start at the
  // search step, as on the Team screen; officials go straight to the form.
  const signalOnMount = useRef(addPlayerSignal)
  useEffect(() => {
    if (addPlayerSignal !== signalOnMount.current) {
      setModal({
        kind: tab === 'officials' ? 'official-add' : 'player-search',
      })
    }
    // `tab` is read, not depended on: a tab switch must not re-open the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addPlayerSignal])

  const officialTypes = useSelector<Array<{ id: number; name_full?: string }>>(
    (state) => getTeamOfficialTypesBySportId(state, sportId)
  )
  const positionOptions = usePositionOptions(sportId)

  // The grid's own permission, not the competition's — a user may administer the
  // competition without holding roster write access. Routed through the shared
  // helpers so this matches the Team screen exactly.
  const {
    canEditRoster,
    canAdmin,
    canAddPlayer,
    canAddOfficial,
    canImportRoster,
    importDisabledReason,
    canExportRoster,
    rosterId: capabilityRosterId,
    isLoading,
  } = useRosterPermissions({ canEdit, organization })

  useEffect(() => {
    onCapabilitiesChange?.({
      canAddPlayer,
      canAddOfficial,
      canImportRoster,
      importDisabledReason,
      canExportRoster,
      rosterId: capabilityRosterId,
      isLoading,
    })
  }, [
    canAddPlayer,
    canAddOfficial,
    canImportRoster,
    importDisabledReason,
    canExportRoster,
    capabilityRosterId,
    isLoading,
    onCapabilitiesChange,
  ])

  const { savePlayerField, pendingIds } = useInlinePlayerEdit({
    teamId,
    rosterId: selectedRosterId,
  })

  const { saveOfficialField, pendingIds: pendingOfficialIds } =
    useInlineOfficialEdit({
      teamId,
      rosterId: selectedRosterId,
      rawOfficials: teamOfficials ?? [],
    })

  const playerRows: RosterPlayerRow[] = (players ?? []).map(toRosterPlayerRow)
  const spareRows: RosterPlayerRow[] = (affiliates ?? []).map(toRosterPlayerRow)
  const officialRows: RosterOfficialRow[] = (teamOfficials ?? []).map(
    toRosterOfficialRow
  )

  // Push counts up for the header's stat strip. Depends on the three lengths,
  // not the arrays, so a refetch returning equal-length lists does not re-fire.
  const playerCount = playerRows.length
  const spareCount = spareRows.length
  const officialCount = officialRows.length
  useEffect(() => {
    onCountsChange?.({
      players: playerCount,
      spares: spareCount,
      officials: officialCount,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerCount, spareCount, officialCount])

  /** The raw context row for an id — the modals and the diff need the full object. */
  const rawPlayer = (id: string) =>
    [...(players ?? []), ...(affiliates ?? [])].find(
      (player: { id: string }) => player.id === id
    )

  const handlePlayerAction = (
    action: RosterRowAction,
    row: RosterPlayerRow
  ) => {
    const player = rawPlayer(row.id)
    if (!player) {
      return
    }
    // Switched rather than built from a template string, so the union actually
    // narrows and a new action cannot silently produce an unhandled kind.
    if (action === 'edit') {
      setModal({ kind: 'player-edit', player })
    } else if (action === 'resend') {
      setModal({ kind: 'player-resend', player })
    } else if (action === 'family') {
      setModal({ kind: 'player-family', player })
    } else {
      setModal({ kind: 'player-remove', player })
    }
  }

  const handleOfficialAction = (
    action: RosterRowAction,
    row: RosterOfficialRow
  ) => {
    const official = (teamOfficials ?? []).find(
      (item: { id: string }) => item.id === row.id
    )
    if (!official) {
      return
    }
    if (action === 'edit') {
      setModal({ kind: 'official-edit', official })
    } else if (action === 'resend') {
      setModal({ kind: 'official-resend', official })
    } else if (action === 'family') {
      // Unreachable: the officials grid does not pass `canManageFamily`, so the
      // item is never rendered. Guarded anyway so a future opt-in cannot
      // silently fall through to remove.
    } else {
      setModal({ kind: 'official-remove', official })
    }
  }

  const playerColumns = useRosterPlayersColumns({
    canEdit: canEditRoster,
    canAdmin,
    showWaiver: shouldShowWaiver,
    positionOptions,
    onEditField: (row, field, value, currentValue) => {
      const player = rawPlayer(row.id)
      if (player) {
        // Fire and forget: the hook owns its own pending + error handling, and
        // AG Grid's valueSetter is synchronous.
        savePlayerField(player, field, value, currentValue).catch(() => {})
      }
    },
    onRowAction: handlePlayerAction,
  })

  const officialColumns = useRosterOfficialsColumns({
    canEdit: canEditRoster,
    officialTypes: officialTypes ?? [],
    onEditField: (row, field, value, currentValue) => {
      // Fire and forget, as above — AG Grid's valueSetter is synchronous.
      saveOfficialField(row, field, value, currentValue).catch(() => {})
    },
    onRowAction: handleOfficialAction,
  })

  // Column chooser, one set per tab (Players and Spares share theirs). No
  // storageKey: the choice lasts for this visit only and resets on refresh.
  const playerVisibility = useColumnVisibility({
    columns: chooserColumnsOf(playerColumns, ROSTER_LOCKED_COLUMN_IDS),
  })
  const officialVisibility = useColumnVisibility({
    columns: chooserColumnsOf(officialColumns, ROSTER_LOCKED_COLUMN_IDS),
  })
  const activeVisibility =
    tab === 'players' ? playerVisibility : officialVisibility
  const shownPlayerColumns = applyHiddenColumns(
    playerColumns,
    playerVisibility.isHidden
  )
  const shownOfficialColumns = applyHiddenColumns(
    officialColumns,
    officialVisibility.isHidden
  )

  return (
    <div css={sectionStyle}>
      <div css={tabBarStyle}>
        <div css={tabsStyle} role='tablist'>
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'players'}
            css={tabStyle(tab === 'players')}
            onClick={() => selectTab('players')}
          >
            {capitalizeWords(translate('common:player_plural'))}
            <span css={countStyle(tab === 'players')}>
              {playerRows.length + spareRows.length}
            </span>
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={tab === 'officials'}
            css={tabStyle(tab === 'officials')}
            onClick={() => selectTab('officials')}
          >
            {capitalizeWords(translate('common:teamOfficial_plural'))}
            <span css={countStyle(tab === 'officials')}>
              {officialRows.length}
            </span>
          </button>
        </div>
        <div css={chooserStyle}>
          <ColumnChooser
            label={capitalizeWords(translate('common:gameGridColumnsButton'))}
            title={capitalizeWords(translate('common:gameGridColumnsTitle'))}
            columns={activeVisibility.columns}
            onToggle={activeVisibility.setColumnVisible}
            onReset={activeVisibility.resetColumns}
            data-testid='operations-roster-column-chooser'
          />
        </div>
      </div>

      {tab === 'players' ? (
        <>
          <RosterGrid
            rows={playerRows}
            columns={shownPlayerColumns}
            pendingIds={pendingIds}
            loading={loading}
            testId='operations-roster-players'
            emptyLabel={capitalizeWords(
              translate('common:noEntityYet', {
                entity: translate('common:player_plural'),
              })
            )}
          />

          {spareRows.length > 0 && (
            <>
              <h4 css={subLabelStyle}>
                {capitalizeWords(translate('common:spare_plural'))}
              </h4>
              <RosterGrid
                rows={spareRows}
                columns={shownPlayerColumns}
                pendingIds={pendingIds}
                loading={false}
                testId='operations-roster-spares'
              />
            </>
          )}
        </>
      ) : (
        <RosterGrid
          rows={officialRows}
          columns={shownOfficialColumns}
          pendingIds={pendingOfficialIds}
          loading={loading}
          testId='operations-roster-officials'
          emptyLabel={capitalizeWords(
            translate('common:noEntityYet', {
              entity: translate('common:teamOfficial_plural'),
            })
          )}
        />
      )}

      <RosterModals
        state={modal}
        teamId={teamId}
        teamName={teamName}
        rosterId={selectedRosterId}
        sportId={sportId}
        canAdmin={canAdmin}
        organization={organization}
        onStateChange={setModal}
        onClose={() => setModal({ kind: 'none' })}
        hostZIndex={hostZIndex}
      />
    </div>
  )
}

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`

/**
 * No `overflow-x: auto` here — there are only ever two tabs, so it can never
 * scroll, but the property still paints a scrollbar track in the panel corner.
 */
/* Tabs on the left, the column chooser on the right, one underline across. */
const tabBarStyle = css`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

const tabsStyle = css`
  display: flex;
  gap: 22px;
`

/* Centred in the bar, clear of the underline the tabs sit on. */
const chooserStyle = css`
  align-self: center;
  padding: 4px 0;
`

const tabStyle = (active: boolean) => css`
  ${paragraphSmall}
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 0 10px;
  border: none;
  background: none;
  cursor: pointer;
  white-space: nowrap;
  color: ${active ? colors.NEUTRAL_0 : colors.NEUTRAL_100};
  border-bottom: 2px solid ${active ? colors.PRIMARY : 'transparent'};
  margin-bottom: -1px;

  &:hover {
    color: ${colors.NEUTRAL_0};
  }
`

/**
 * A fixed circle at one OR two digits — both must render the same size, so the
 * box is square with no horizontal padding. Padding is what previously stretched
 * "12" into an oval while "1" stayed round. Three digits or more push it into a
 * pill rather than shrinking the type.
 */
const COUNT_SIZE = 20

const countStyle = (active: boolean) => css`
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: ${COUNT_SIZE}px;
  height: ${COUNT_SIZE}px;
  padding: 0;
  border-radius: ${COUNT_SIZE / 2}px;
  font-size: 10px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  background: ${active ? colors.PRIMARY : colors.SECONDARY_300};
  color: ${active ? colors.PRIMARY_TEXT : colors.NEUTRAL_50};
`

/* Body font, not `font.title`: the condensed title face at 10px was the main
   reason this was hard to read. */
const subLabelStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_0};
  margin: 16px 0 8px;
`

export default RosterSection
